import { useState, useEffect, useRef, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Phone, MessageSquare, Calendar, Clock, Shield, Zap,
  Menu, X, CheckCircle, Star, ArrowRight, Link2,
  Mic, PhoneCall, Activity, Bot, PhoneOff, TrendingDown,
  TrendingUp, BarChart2, Users, Globe, Lock, Headphones, Mail
} from "lucide-react";
import Vapi from "@vapi-ai/web";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Voice AI Singleton ────────────────────────────────────────────────────
const _API_KEY = process.env.REACT_APP_VAPI_API_KEY || "";
const _ASST_ID = process.env.REACT_APP_VAPI_ASSISTANT_ID || "";

let _vapiInst = null;
function getVapi(forceNew = false) {
  if (!_vapiInst || forceNew) {
    _vapiInst = new Vapi(_API_KEY);
  }
  return _vapiInst;
}

// ─── Voice Widget ──────────────────────────────────────────────────────────
function VoiceWidget() {
  const [callState, setCallState] = useState("idle"); // idle | connecting | active | ended
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [volume, setVolume] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);

  // Register / unregister named handlers so no duplicates on re-mount
  useEffect(() => {
    const vapi = getVapi();

    function onCallStart() { setCallState("active"); setErrorMsg(null); }
    function onCallEnd() { setCallState("idle"); setIsSpeaking(false); setVolume(0); }
    function onSpeechStart() { setIsSpeaking(true); }
    function onSpeechEnd() { setIsSpeaking(false); }
    function onVolume(lvl) { setVolume(lvl); }
    function onMessage(msg) {
      if (msg?.type === "transcript" && msg?.transcriptType === "final") {
        setTranscript(prev => [...prev.slice(-3), { role: msg.role, text: msg.transcript }]);
      }
    }
    function onError(err) {
      let msg = err?.error?.message || err?.error?.msg || err?.message || "Connection error";
      // Friendly mic error
      if (msg.toLowerCase().includes("notfound") || msg.toLowerCase().includes("no mic") || msg.toLowerCase().includes("device not found")) {
        msg = "No microphone found. Please connect a microphone and try again.";
      }
      if (msg.toLowerCase().includes("notallowed") || msg.toLowerCase().includes("permission")) {
        msg = "Microphone access denied. Please allow microphone permission in your browser.";
      }
      setErrorMsg(msg);
      setCallState("idle");
      toast.error(msg);
    }
    function onCallStartFailed(data) {
      const msg = data?.error || data?.message || "Failed to connect. Please try again.";
      setErrorMsg(msg);
      setCallState("idle");
      toast.error(msg);
    }

    vapi.on("call-start", onCallStart);
    vapi.on("call-end", onCallEnd);
    vapi.on("speech-start", onSpeechStart);
    vapi.on("speech-end", onSpeechEnd);
    vapi.on("volume-level", onVolume);
    vapi.on("message", onMessage);
    vapi.on("error", onError);
    vapi.on("call-start-failed", onCallStartFailed);

    return () => {
      vapi.off("call-start", onCallStart);
      vapi.off("call-end", onCallEnd);
      vapi.off("speech-start", onSpeechStart);
      vapi.off("speech-end", onSpeechEnd);
      vapi.off("volume-level", onVolume);
      vapi.off("message", onMessage);
      vapi.off("error", onError);
      vapi.off("call-start-failed", onCallStartFailed);
    };
  }, []);

  const startCall = useCallback(async () => {
    setCallState("connecting");
    setErrorMsg(null);
    setTranscript([]);
    try {
      // Force a fresh instance if previous call left it in bad state
      const vapi = getVapi();
      const result = await vapi.start(_ASST_ID);
      // start() returns null if already started — reset and retry once
      if (result === null && vapi.started) {
        vapi.stop();
        await new Promise(r => setTimeout(r, 300));
        await getVapi(true).start(_ASST_ID);
      }
    } catch (err) {
      let msg = err?.message || "Failed to start call";
      if (msg.toLowerCase().includes("notfound") || msg.toLowerCase().includes("device not found")) {
        msg = "No microphone found. Please connect a microphone and try again.";
      }
      if (msg.toLowerCase().includes("notallowed") || msg.toLowerCase().includes("permission")) {
        msg = "Microphone access denied. Please allow microphone permission in your browser.";
      }
      setErrorMsg(msg);
      setCallState("idle");
      toast.error(msg);
    }
  }, []);

  const stopCall = useCallback(() => {
    getVapi().stop();
    setCallState("idle");
    setIsSpeaking(false);
    setVolume(0);
  }, []);

  return (
    <div className="flex flex-col items-center gap-5 w-full">
      {/* Mic / End button */}
      <div className="relative flex items-center justify-center">
        {callState === "active" && (
          <>
            <motion.div className="absolute rounded-full border border-cyan-400/30"
              style={{ width: 110, height: 110 }}
              animate={{ scale: [1, 1.4], opacity: [0.5, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }} />
            <motion.div className="absolute rounded-full border border-cyan-400/20"
              style={{ width: 110, height: 110 }}
              animate={{ scale: [1, 1.7], opacity: [0.4, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut", delay: 0.4 }} />
          </>
        )}

        {(callState === "idle" || callState === "ended") && (
          <motion.button data-testid="voice-start-btn" onClick={startCall}
            whileHover={{ scale: 1.07 }} whileTap={{ scale: 0.93 }}
            className="relative w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-500 flex items-center justify-center shadow-lg glow-cyan cursor-pointer">
            <Mic className="w-8 h-8 text-[#020408]" />
          </motion.button>
        )}
        {callState === "connecting" && (
          <div data-testid="voice-connecting-indicator"
            className="w-20 h-20 rounded-full bg-cyan-400/30 border border-cyan-400/40 flex items-center justify-center cursor-wait">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
              <Activity className="w-8 h-8 text-cyan-300" />
            </motion.div>
          </div>
        )}
        {callState === "active" && (
          <motion.button data-testid="voice-end-btn" onClick={stopCall}
            whileHover={{ scale: 1.07 }} whileTap={{ scale: 0.93 }}
            className="relative w-20 h-20 rounded-full bg-red-500 flex items-center justify-center shadow-lg cursor-pointer"
            style={{ boxShadow: "0 0 20px rgba(239,68,68,0.4)" }}>
            <PhoneOff className="w-8 h-8 text-white" />
          </motion.button>
        )}
      </div>

      {/* Status text */}
      <div className="text-center min-h-[48px] flex flex-col items-center justify-center gap-1.5 px-2">
        {(callState === "idle" || callState === "ended") && !errorMsg && (
          <p className="text-sm text-slate-300">Click the mic to speak with Careline AI</p>
        )}
        {callState === "connecting" && (
          <p className="text-sm text-cyan-400 animate-pulse font-medium">Connecting...</p>
        )}
        {callState === "active" && (
          <>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-sm text-green-400 font-medium">
                {isSpeaking ? "AI is speaking..." : "Listening to you..."}
              </span>
            </div>
            <button data-testid="voice-end-text-btn" onClick={stopCall}
              className="text-xs text-red-400 hover:text-red-300 underline underline-offset-2 transition-colors">
              End call
            </button>
          </>
        )}
        {errorMsg && (
          <div className="flex flex-col items-center gap-1">
            <p className="text-xs text-red-400 max-w-[220px] text-center leading-relaxed">{errorMsg}</p>
            <button onClick={() => setErrorMsg(null)}
              className="text-xs text-cyan-400 hover:text-cyan-300 underline underline-offset-2">
              Try again
            </button>
          </div>
        )}
      </div>

      {/* Volume bars (active only) */}
      {callState === "active" && (
        <div className="flex items-end gap-[3px] h-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <motion.div key={i} className="w-[3px] rounded-full bg-cyan-400"
              animate={{
                height: isSpeaking
                  ? [4, 6 + ((i % 4) * 4) + volume * 10, 4]
                  : [4, 4 + volume * 14 * (0.4 + (i % 3) * 0.2), 4]
              }}
              transition={{ duration: 0.35, repeat: Infinity, delay: i * 0.05, ease: "easeInOut" }} />
          ))}
        </div>
      )}

      {/* Live transcript */}
      {callState === "active" && transcript.length > 0 && (
        <div className="w-full max-w-[260px] bg-white/5 border border-white/10 rounded-xl p-3 space-y-1.5">
          {transcript.map((t, i) => (
            <p key={i} className={`text-xs leading-relaxed ${t.role === "assistant" ? "text-cyan-300" : "text-slate-300"}`}>
              <span className="font-semibold">{t.role === "assistant" ? "Careline" : "You"}:</span>{" "}{t.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────
function Counter({ end, suffix = "", duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let cur = 0;
    const step = end / (duration * 60);
    const t = setInterval(() => {
      cur += step;
      if (cur >= end) { setCount(end); clearInterval(t); }
      else setCount(Math.floor(cur));
    }, 1000 / 60);
    return () => clearInterval(t);
  }, [inView, end, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
}

function FadeIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
}

// ─── Integration Tile ───────────────────────────────────────────────────────
function IntegrationTile({ name, category, color, abbr }) {
  return (
    <div className="glass-card rounded-xl p-4 flex items-center gap-3 hover:border-white/20 transition-all duration-200 group">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0 font-bold text-white text-xs`}>
        {abbr}
      </div>
      <div>
        <div className="text-sm font-medium text-white">{name}</div>
        <div className="text-xs text-slate-500">{category}</div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [clinicInfo, setClinicInfo] = useState({ vapi_phone_number: "+1 (501) 451 0490" });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/public/clinic-info`).then(r => r.json()).then(d => setClinicInfo(d)).catch(() => {});
  }, []);

  const features = [
    { icon: Phone, title: "24/7 Voice AI Calls", desc: "Never miss a patient call. Our AI answers instantly, day or night, with natural human-like conversation.", color: "from-cyan-500/20 to-cyan-500/5", accent: "cyan" },
    { icon: Calendar, title: "Smart Appointment Booking", desc: "Patients book, reschedule, and confirm appointments seamlessly through voice — no hold music.", color: "from-violet-500/20 to-violet-500/5", accent: "violet" },
    { icon: MessageSquare, title: "WhatsApp Integration", desc: "Reach patients on WhatsApp via Twilio and Meta — send reminders, confirmations, and follow-ups.", color: "from-green-500/20 to-green-500/5", accent: "green" },
    { icon: Bot, title: "AI-Powered Intelligence", desc: "Understands medical terminology, handles complex queries, and escalates to human staff when needed.", color: "from-amber-500/20 to-amber-500/5", accent: "amber" },
    { icon: Shield, title: "HIPAA-Ready Security", desc: "Patient data is encrypted, access-controlled, and handled with full healthcare compliance standards.", color: "from-blue-500/20 to-blue-500/5", accent: "blue" },
    { icon: BarChart2, title: "Real-Time Analytics", desc: "Monitor every call, appointment, and interaction from a powerful admin dashboard with live insights.", color: "from-rose-500/20 to-rose-500/5", accent: "rose" },
  ];

  const steps = [
    { num: "01", title: "Connect Your Clinic", desc: "Link your AI assistant and configure your clinic details in under 10 minutes." },
    { num: "02", title: "AI Handles Inbound Calls", desc: "Patients call your number. Careline greets them, understands their needs, and acts instantly." },
    { num: "03", title: "Appointments Auto-Booked", desc: "Voice-confirmed bookings appear in your dashboard automatically — zero manual work." },
    { num: "04", title: "Multi-Channel Follow-up", desc: "Confirmation messages sent via WhatsApp and SMS to reduce no-shows by up to 40%." },
  ];

  const metrics = [
    { icon: TrendingDown, label: "Call Abandonment", value: "-56%", color: "text-green-400", bg: "bg-green-400/10", border: "border-green-400/20" },
    { icon: Clock, label: "Wait Times", value: "-58%", color: "text-cyan-400", bg: "bg-cyan-400/10", border: "border-cyan-400/20" },
    { icon: TrendingUp, label: "Appointments Booked", value: "+47%", color: "text-violet-400", bg: "bg-violet-400/10", border: "border-violet-400/20" },
    { icon: BarChart2, label: "Automation Rate", value: "85%+", color: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/20" },
    { icon: Users, label: "Agent Productivity", value: "+40%", color: "text-rose-400", bg: "bg-rose-400/10", border: "border-rose-400/20" },
    { icon: Zap, label: "Operational Costs", value: "-35%", color: "text-blue-400", bg: "bg-blue-400/10", border: "border-blue-400/20" },
  ];

  const integrations = [
    // EHR/EMR
    { name: "Epic EMR", category: "EHR / EMR", color: "bg-red-600", abbr: "EP" },
    { name: "Athena Health", category: "EHR / EMR", color: "bg-blue-600", abbr: "AH" },
    { name: "eClinicalWorks", category: "EHR / EMR", color: "bg-indigo-600", abbr: "EC" },
    { name: "Cerner (Oracle)", category: "EHR / EMR", color: "bg-red-700", abbr: "CE" },
    { name: "Kareo", category: "EHR / EMR", color: "bg-teal-600", abbr: "KA" },
    { name: "DrChrono", category: "EHR / EMR", color: "bg-emerald-600", abbr: "DC" },
    // Scheduling
    { name: "Calendly", category: "Scheduling", color: "bg-cyan-600", abbr: "CL" },
    { name: "Google Calendar", category: "Scheduling", color: "bg-blue-500", abbr: "GC" },
    { name: "Microsoft Outlook", category: "Scheduling", color: "bg-sky-600", abbr: "MO" },
    { name: "Acuity Scheduling", category: "Scheduling", color: "bg-violet-600", abbr: "AS" },
    // Communication
    { name: "Twilio Voice", category: "Communication", color: "bg-red-500", abbr: "TW" },
    { name: "WhatsApp (Twilio)", category: "Messaging", color: "bg-green-600", abbr: "WA" },
    { name: "Meta WhatsApp", category: "Messaging", color: "bg-green-700", abbr: "ME" },
    { name: "Twilio SMS", category: "Communication", color: "bg-red-600", abbr: "SM" },
    // CRM
    { name: "Salesforce", category: "CRM", color: "bg-sky-500", abbr: "SF" },
    { name: "HubSpot", category: "CRM", color: "bg-orange-600", abbr: "HS" },
    { name: "Zoho CRM", category: "CRM", color: "bg-red-500", abbr: "ZO" },
    // Telehealth
    { name: "Zoom Health", category: "Telehealth", color: "bg-blue-600", abbr: "ZM" },
    { name: "Microsoft Teams", category: "Telehealth", color: "bg-indigo-500", abbr: "MT" },
    { name: "Doxy.me", category: "Telehealth", color: "bg-teal-500", abbr: "DX" },
    // Other
    { name: "Stripe Payments", category: "Billing", color: "bg-violet-600", abbr: "ST" },
    { name: "Vapi.ai", category: "Voice AI", color: "bg-cyan-500", abbr: "VP" },
    { name: "OpenAI", category: "AI Engine", color: "bg-slate-600", abbr: "OA" },
    { name: "Google Meet", category: "Telehealth", color: "bg-green-500", abbr: "GM" },
  ];

  const trustedBy = [
    "Sunrise Medical", "CareFirst Clinic", "MedBridge Health",
    "PrimaCare Group", "WellPath Medical", "United Regional",
    "Sunrise Medical", "CareFirst Clinic", "MedBridge Health",
    "PrimaCare Group", "WellPath Medical", "United Regional",
  ];

  const testimonials = [
    { name: "Dr. Sarah Chen", role: "Internal Medicine, Boston", text: "Our no-show rate dropped 40% after implementing this. Patients love the instant response.", rating: 5 },
    { name: "Dr. James Okafor", role: "Family Practice, Atlanta", text: "Set it up in a day. The AI handles 80% of our inbound calls without any intervention.", rating: 5 },
    { name: "Dr. Priya Sharma", role: "Pediatrics, Chicago", text: "WhatsApp reminders alone paid for the subscription. Incredible ROI for our practice.", rating: 5 },
  ];

  return (
    <div className="landing-bg min-h-screen text-white overflow-x-hidden">

      {/* ── Inline styles for marquee and dot-grid ── */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track {
          display: flex;
          width: max-content;
          animation: marquee 28s linear infinite;
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
        .dot-grid {
          background-image: radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px);
          background-size: 28px 28px;
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 40px 8px rgba(34,211,238,0.15), 0 0 80px 20px rgba(139,92,246,0.08); }
          50% { box-shadow: 0 0 60px 16px rgba(34,211,238,0.28), 0 0 120px 32px rgba(139,92,246,0.14); }
        }
        .cta-glow {
          animation: pulse-glow 3.5s ease-in-out infinite;
        }
        .feature-card-hover {
          transition: box-shadow 0.3s ease, border-color 0.3s ease, transform 0.3s ease;
        }
        .feature-card-hover:hover {
          transform: translateY(-3px);
        }
        .feature-card-hover.cyan-hover:hover { box-shadow: 0 0 32px 4px rgba(34,211,238,0.12); border-color: rgba(34,211,238,0.25); }
        .feature-card-hover.violet-hover:hover { box-shadow: 0 0 32px 4px rgba(139,92,246,0.12); border-color: rgba(139,92,246,0.25); }
        .feature-card-hover.green-hover:hover { box-shadow: 0 0 32px 4px rgba(74,222,128,0.10); border-color: rgba(74,222,128,0.22); }
        .feature-card-hover.amber-hover:hover { box-shadow: 0 0 32px 4px rgba(251,191,36,0.10); border-color: rgba(251,191,36,0.22); }
        .feature-card-hover.blue-hover:hover { box-shadow: 0 0 32px 4px rgba(96,165,250,0.10); border-color: rgba(96,165,250,0.22); }
        .feature-card-hover.rose-hover:hover { box-shadow: 0 0 32px 4px rgba(251,113,133,0.10); border-color: rgba(251,113,133,0.22); }
        .widget-glow {
          box-shadow: 0 0 0 1px rgba(34,211,238,0.18), 0 0 32px 4px rgba(34,211,238,0.08), 0 0 64px 8px rgba(139,92,246,0.06);
        }
      `}</style>

      {/* ── Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl"
        style={{ background: "rgba(2,4,8,0.82)", borderBottom: "1px solid rgba(34,211,238,0.10)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-600 flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg font-outfit tracking-tight">Careline</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            {["Features", "Integrations", "How It Works", "Demo", "Reviews"].map(item => (
              <a key={item} href={`#${item.toLowerCase().replace(" ", "-")}`}
                className="hover:text-white transition-colors">{item}</a>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button data-testid="nav-login-btn" onClick={() => navigate("/admin/login")}
              className="text-sm text-slate-300 hover:text-white px-4 py-2 transition-colors">Admin Login</button>
            <button data-testid="nav-get-started-btn" onClick={() => navigate("/admin/login")}
              className="text-sm bg-cyan-400 text-black font-semibold px-5 py-2 rounded-full hover:bg-cyan-300 transition-all duration-200 hover:scale-105">
              Get Started
            </button>
          </div>
          <button data-testid="mobile-menu-btn" className="md:hidden text-slate-400 hover:text-white" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-[#0F1116] border-t border-white/5 px-4 py-4 flex flex-col gap-4 text-sm text-slate-300">
            {["Features", "Integrations", "How It Works", "Demo"].map(item => (
              <a key={item} href={`#${item.toLowerCase().replace(" ", "-")}`} onClick={() => setMenuOpen(false)}>{item}</a>
            ))}
            <button onClick={() => navigate("/admin/login")} className="text-left text-cyan-400">Admin Login</button>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden">
        {/* Dot grid overlay */}
        <div className="absolute inset-0 dot-grid opacity-100 pointer-events-none" />

        {/* Background image */}
        <div className="absolute inset-0"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1714779573250-36242918e044?auto=format&fit=crop&w=1920&q=40)`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.07 }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020408]/60 via-[#020408]/80 to-[#020408]" />

        {/* Animated gradient orbs */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{ width: 520, height: 520, top: "8%", left: "10%", background: "radial-gradient(circle, rgba(34,211,238,0.13) 0%, transparent 70%)", filter: "blur(40px)" }}
          animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1, 1.08, 1] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{ width: 440, height: 440, top: "20%", right: "8%", background: "radial-gradient(circle, rgba(139,92,246,0.13) 0%, transparent 70%)", filter: "blur(40px)" }}
          animate={{ x: [0, -25, 0], y: [0, 25, 0], scale: [1, 1.06, 1] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
        />
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{ width: 300, height: 300, bottom: "15%", left: "40%", background: "radial-gradient(circle, rgba(139,92,246,0.09) 0%, transparent 70%)", filter: "blur(50px)" }}
          animate={{ x: [0, 20, 0], y: [0, 15, 0] }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 3 }}
        />

        <div className="relative max-w-5xl mx-auto text-center">
          {/* Badge pill */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 text-xs font-semibold mb-8 backdrop-blur-sm"
            style={{ boxShadow: "0 0 20px rgba(34,211,238,0.1)" }}>
            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
            AI-Powered 24/7 Voice Receptionist
            <span className="ml-1 bg-cyan-400/20 text-cyan-300 text-[10px] px-2 py-0.5 rounded-full font-bold tracking-wide">LIVE</span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-8xl font-black font-outfit tracking-tight leading-[1.05] mb-6">
            Stop Losing Patients{" "}
            <br className="hidden sm:block" />
            <span className="gradient-text">to Missed Calls</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Careline answers every call, books every appointment, 24/7 —{" "}
            <span className="text-slate-200 font-medium">while your staff focuses on care.</span>
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button data-testid="hero-cta-btn" onClick={() => navigate("/admin/login")}
              className="flex items-center justify-center gap-2 bg-cyan-400 text-black font-bold px-9 py-4 rounded-full hover:bg-cyan-300 transition-all duration-200 hover:scale-105 text-base"
              style={{ boxShadow: "0 0 32px rgba(34,211,238,0.35)" }}>
              Book a Demo <ArrowRight className="w-4 h-4" />
            </button>
            <a href={`tel:${clinicInfo.vapi_phone_number}`} data-testid="hero-demo-call-btn"
              className="flex items-center justify-center gap-2 border border-white/20 text-white font-medium px-9 py-4 rounded-full hover:border-cyan-400/50 hover:text-cyan-400 transition-all duration-200 text-base backdrop-blur-sm">
              <PhoneCall className="w-4 h-4" />
              Call Demo: {clinicInfo.vapi_phone_number || "+1 (501) 451 0490"}
            </a>
          </motion.div>

          {/* Voice Widget — premium glass card */}
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            id="demo"
            className="relative max-w-sm mx-auto rounded-2xl p-[1px] widget-glow"
            style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.22) 0%, rgba(139,92,246,0.18) 50%, rgba(34,211,238,0.10) 100%)" }}>
            <div className="relative rounded-2xl p-8 overflow-hidden"
              style={{ background: "linear-gradient(160deg, rgba(15,17,22,0.97) 0%, rgba(10,13,18,0.98) 100%)", backdropFilter: "blur(24px)" }}>
              {/* Inner glow accent */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-6 text-center">Live Voice Demo</p>
              <VoiceWidget />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Trusted By — Marquee ── */}
      <section className="py-12 border-y border-white/5 bg-[#0A0D12] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 mb-6">
          <p className="text-xs text-slate-500 uppercase tracking-widest text-center">Trusted by leading clinics</p>
        </div>
        <div className="overflow-hidden relative">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
            style={{ background: "linear-gradient(to right, #0A0D12, transparent)" }} />
          <div className="absolute right-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
            style={{ background: "linear-gradient(to left, #0A0D12, transparent)" }} />
          <div className="marquee-track">
            {trustedBy.map((name, idx) => (
              <div key={idx} className="flex items-center gap-2.5 mx-8 opacity-50 hover:opacity-90 transition-opacity flex-shrink-0">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 flex items-center justify-center">
                  <Headphones className="w-3.5 h-3.5 text-slate-300" />
                </div>
                <span className="text-sm font-semibold text-slate-300 whitespace-nowrap">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="py-20 bg-[#0F1116]/50 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(34,211,238,0.04) 0%, transparent 70%)" }} />
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center relative">
          {[
            { label: "Calls Handled Daily", value: 1200, suffix: "+" },
            { label: "Appointments Booked", value: 98, suffix: "%" },
            { label: "Clinics Active", value: 500, suffix: "+" },
            { label: "Avg Response Time", value: 2, suffix: "s" },
          ].map((s, i) => (
            <FadeIn key={s.label} delay={i * 0.1}>
              <div className="text-5xl sm:text-6xl font-black font-outfit gradient-text tracking-tight">
                <Counter end={s.value} suffix={s.suffix} />
              </div>
              <div className="text-xs text-slate-500 mt-2 uppercase tracking-wider font-medium">{s.label}</div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── Performance Metrics ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-green-400/30 bg-green-400/10 text-green-400 text-xs mb-4">
                <BarChart2 className="w-3 h-3" /> Proven Results
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold font-outfit mb-4">
                The Proof is in the Performance
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Real outcomes from clinics that replaced manual front-desk workflows with Careline AI.
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {metrics.map((m, i) => (
              <FadeIn key={m.label} delay={i * 0.07}>
                <div className={`glass-card rounded-xl p-6 flex items-center gap-4 border ${m.border} transition-all duration-300 hover:scale-[1.02]`}
                  style={{ transition: "box-shadow 0.3s ease, transform 0.3s ease" }}>
                  <div className={`w-12 h-12 rounded-xl ${m.bg} flex items-center justify-center flex-shrink-0`}>
                    <m.icon className={`w-6 h-6 ${m.color}`} />
                  </div>
                  <div>
                    <div className={`text-3xl font-black font-outfit ${m.color}`}>{m.value}</div>
                    <div className="text-sm text-slate-400">{m.label}</div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-20 px-4 bg-[#0F1116]/50">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-violet-400/30 bg-violet-400/10 text-violet-400 text-xs mb-4">
                <Zap className="w-3 h-3" /> Everything You Need
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold font-outfit mb-4">
                AI Agents for Modern Clinics
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Enterprise-grade conversational AI that automatically resolves 85%+ of repetitive tasks.
              </p>
            </div>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.08}>
                <div className={`glass-card rounded-xl p-6 feature-card-hover ${f.accent}-hover`}>
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${f.color} flex items-center justify-center mb-4`}>
                    <f.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold font-outfit text-white mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Integrations ── */}
      <section id="integrations" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-400 text-xs mb-4">
                <Link2 className="w-3 h-3" /> 20+ Integrations
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold font-outfit mb-4">
                Connect to your{" "}
                <span className="gradient-text">favorite tools</span>
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                From critical EHR/EMR data sources to scheduling, messaging, and billing systems.
                Careline plugs into your existing stack instantly.
              </p>
            </div>
          </FadeIn>

          {/* Category tabs */}
          {[
            {
              label: "EHR / EMR",
              items: integrations.filter(i => i.category === "EHR / EMR"),
              accent: "text-red-400",
            },
            {
              label: "Scheduling",
              items: integrations.filter(i => i.category === "Scheduling"),
              accent: "text-cyan-400",
            },
            {
              label: "Messaging",
              items: integrations.filter(i => ["Messaging", "Communication"].includes(i.category)),
              accent: "text-green-400",
            },
            {
              label: "CRM & Billing",
              items: integrations.filter(i => ["CRM", "Billing"].includes(i.category)),
              accent: "text-amber-400",
            },
            {
              label: "Telehealth & AI",
              items: integrations.filter(i => ["Telehealth", "Voice AI", "AI Engine"].includes(i.category)),
              accent: "text-violet-400",
            },
          ].map((cat, ci) => (
            <FadeIn key={cat.label} delay={ci * 0.08}>
              <div className="mb-8">
                <div className={`text-xs font-semibold uppercase tracking-widest ${cat.accent} mb-3`}>{cat.label}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {cat.items.map(intg => (
                    <IntegrationTile key={intg.name} {...intg} />
                  ))}
                </div>
              </div>
            </FadeIn>
          ))}

          <FadeIn>
            <div className="mt-8 glass-card rounded-xl p-6 flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="font-semibold font-outfit text-white text-lg mb-2">Don't see your system?</h3>
                <p className="text-sm text-slate-400">Careline supports any system with REST APIs or webhooks. Custom integrations available for enterprise plans.</p>
              </div>
              <button data-testid="integrations-contact-btn" onClick={() => navigate("/admin/login")}
                className="flex items-center gap-2 bg-white/10 border border-white/20 text-white px-5 py-2.5 rounded-full hover:bg-white/15 transition-all text-sm font-medium flex-shrink-0">
                Request Integration <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Responsible AI ── */}
      <section className="py-20 px-4 bg-[#0F1116]/50">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-400/30 bg-blue-400/10 text-blue-400 text-xs mb-4">
                <Shield className="w-3 h-3" /> Responsible AI
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold font-outfit mb-4">
                AI Engineered with{" "}
                <span className="gradient-text">Patient Safety in Mind</span>
              </h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Globe, title: "Explainability", desc: "Full visibility into the logic pathways behind every AI response and decision made during patient interactions.", color: "from-cyan-500/20 to-cyan-500/5" },
              { icon: Lock, title: "Control", desc: "Contain hallucinations by clearly defining and limiting data sources. Human escalation is always available.", color: "from-violet-500/20 to-violet-500/5" },
              { icon: Shield, title: "Compliance", desc: "HIPAA-compliant architecture with end-to-end encryption, role-based access, and full audit logs.", color: "from-blue-500/20 to-blue-500/5" },
            ].map((p, i) => (
              <FadeIn key={p.title} delay={i * 0.1}>
                <div className="glass-card rounded-xl p-6">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center mb-4`}>
                    <p.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold font-outfit text-white text-lg mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-400 text-xs mb-4">
                <Clock className="w-3 h-3" /> Simple Setup
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold font-outfit mb-4">Up & Running in Minutes</h2>
              <p className="text-slate-400">No training required. No maintenance needed. Just connect and deploy.</p>
            </div>
          </FadeIn>

          {/* Steps with connecting line */}
          <div className="relative">
            {/* Vertical connecting line (desktop hidden, shown on large) */}
            <div className="hidden md:block absolute left-[27px] top-6 bottom-6 w-px"
              style={{ background: "linear-gradient(to bottom, rgba(34,211,238,0.4), rgba(139,92,246,0.4), rgba(34,211,238,0.2), transparent)" }} />

            <div className="grid md:grid-cols-1 gap-4">
              {steps.map((s, i) => (
                <FadeIn key={s.num} delay={i * 0.12}>
                  <div className="glass-card rounded-xl p-6 flex gap-5 md:ml-0 transition-all duration-300 hover:border-white/15 group">
                    {/* Step number dot */}
                    <div className="flex-shrink-0 relative">
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center"
                        style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.18) 0%, rgba(139,92,246,0.18) 100%)", border: "1px solid rgba(34,211,238,0.25)" }}>
                        <span className="font-black font-outfit text-cyan-400 text-sm">{s.num}</span>
                      </div>
                      {/* Dot on the connecting line */}
                      <div className="hidden md:block absolute -left-[14.5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-cyan-400 border-2 border-[#020408]"
                        style={{ boxShadow: "0 0 8px rgba(34,211,238,0.6)" }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold font-outfit text-white mb-1 group-hover:text-cyan-300 transition-colors">{s.title}</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-cyan-400/40 group-hover:text-cyan-400/70 transition-colors flex-shrink-0 mt-0.5" />
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section id="reviews" className="py-20 px-4 bg-[#0F1116]/50">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-4xl font-bold font-outfit mb-4">Trusted by Clinics Nationwide</h2>
              <p className="text-slate-400">Real outcomes from real practices.</p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.1}>
                <div className="glass-card rounded-xl p-6 transition-all duration-300 hover:border-white/15 hover:-translate-y-1">
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed mb-4">"{t.text}"</p>
                  <div>
                    <div className="font-medium text-white text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 px-4 relative overflow-hidden">
        {/* Dramatic gradient background */}
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.07) 0%, rgba(139,92,246,0.10) 40%, rgba(34,211,238,0.06) 100%)" }} />
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />

        {/* Pulsing glow orbs */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{ width: 600, height: 300, top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "radial-gradient(ellipse, rgba(34,211,238,0.10) 0%, transparent 70%)", filter: "blur(40px)" }}
          animate={{ scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{ width: 400, height: 400, top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)", filter: "blur(60px)" }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <FadeIn>
          <div className="max-w-3xl mx-auto text-center relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-300 text-xs font-semibold mb-6">
              <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
              500+ clinics already onboarded
            </div>
            <h2 className="text-4xl sm:text-6xl font-black font-outfit mb-5 leading-tight">
              Every Missed Call is a{" "}
              <span className="gradient-text">Lost Patient</span>
            </h2>
            <p className="text-slate-400 mb-10 text-lg max-w-xl mx-auto leading-relaxed">
              Don't let your competitors out-respond you. Careline answers every call, every time — intelligently.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button data-testid="cta-start-btn" onClick={() => navigate("/admin/login")}
                className="flex items-center justify-center gap-2 bg-cyan-400 text-black font-bold px-9 py-4 rounded-full hover:bg-cyan-300 transition-all hover:scale-105 text-base cta-glow">
                Get Started Today <ArrowRight className="w-4 h-4" />
              </button>
              <a href={`tel:${clinicInfo.vapi_phone_number}`} data-testid="cta-call-btn"
                className="flex items-center justify-center gap-2 border border-white/20 text-white px-9 py-4 rounded-full hover:border-cyan-400/50 hover:text-cyan-400 transition-all text-base backdrop-blur-sm">
                <Phone className="w-4 h-4" /> Try Live Demo Call
              </a>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/5 pt-12 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-10">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-600 flex items-center justify-center">
                  <Phone className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="font-outfit font-bold text-white text-lg">Careline</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                AI-powered virtual receptionist for modern healthcare clinics. 24/7 availability, zero hold times.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Product</h4>
              <div className="flex flex-col gap-2 text-sm text-slate-500">
                <a href="#features" className="hover:text-white transition-colors">Features</a>
                <a href="#integrations" className="hover:text-white transition-colors">Integrations</a>
                <a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a>
                <a href="#demo" className="hover:text-white transition-colors">Live Demo</a>
              </div>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Company</h4>
              <div className="flex flex-col gap-2 text-sm text-slate-500">
                <a href="#reviews" className="hover:text-white transition-colors">Testimonials</a>
                <button onClick={() => navigate("/admin/login")} className="text-left hover:text-white transition-colors">Admin Portal</button>
              </div>
            </div>

            {/* Contact */}
            <div id="contact">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Contact Us</h4>
              <div className="flex flex-col gap-3">
                <a href="mailto:hello@careline.ai" data-testid="footer-contact-email"
                  className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  hello@careline.ai
                </a>
                <a href={`tel:${clinicInfo.vapi_phone_number || "+15014510490"}`}
                  data-testid="footer-contact-phone"
                  className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  {clinicInfo.vapi_phone_number || "+1 (501) 451-0490"}
                </a>
                <button data-testid="footer-contact-btn"
                  onClick={() => navigate("/admin/login")}
                  className="flex items-center gap-2 mt-1 bg-cyan-400 text-black text-xs font-semibold px-4 py-2 rounded-full hover:bg-cyan-300 transition-all w-fit">
                  Book a Demo <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
            <span>© {new Date().getFullYear()} Careline. All rights reserved.</span>
            <div className="flex gap-5">
              <span className="hover:text-slate-400 cursor-default transition-colors">Privacy Policy</span>
              <span className="hover:text-slate-400 cursor-default transition-colors">Terms of Service</span>
              <span className="hover:text-slate-400 cursor-default transition-colors">HIPAA Compliance</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
